/* istanbul ignore file */
import React from 'react'
// @ts-ignore no types
import { Text } from '@adc/ui-adc'
import type { FeatureFlags } from '@adc/ui-adc'
import * as blueprint from '@adc/blueprint'
import moment from 'moment'
import { usePropertyContext } from 'common/context/property'
import { shouldShowSealedBidTreatment } from 'common/utils/privateSeller'
import { SAVE_PROPERTY_CTA_TPS } from '../../../statics'
import AuctionDetailsTemplate, {
  tpsAdditionalContent,
  SavePropertyButton,
} from '../template'
import hasSimilarProperties from '../../../utils/hasSimilarProperties'
import {
  CombinedListingType,
  ShortenedOnlineSegmentType,
  useListingContext,
} from '../../../context/listing'
import SealedBidStatusMessageBox from './sealedBidStatusMessageBox'
import CalendarPopup from './calendar'
import { getERVRow } from './common'

interface DetailContentProps {
  isInTour?: boolean
  listing: CombinedListingType
  segment: ShortenedOnlineSegmentType
  isRedesignEnabled?: boolean
}

const detailContent = ({
  isInTour = false,
  listing,
  segment,
  isRedesignEnabled = false,
}: DetailContentProps) => {
  const provisionalSaleDate = moment(listing.auction?.provisional_sale_date)
  const details = [
    {
      label: (
        <Text color='gray' data-elm-id='pending_re_auction_label'>
          Property has not been sold,
          <br />
          pending re-auction
        </Text>
      ),
    },
    !listing.auction?.provisional_sale_date ||
    provisionalSaleDate.isBefore(moment(segment.start_date))
      ? {
          label: (
            <Text color='gray' data-elm-id='auction_starts_label'>
              Awaiting new auction date
            </Text>
          ),
        }
      : {
          label: (
            <Text color='gray' data-elm-id='auction_starts_label'>
              New Auction Starts
            </Text>
          ),
          value: (
            <Text
              color='gray'
              weight='heavy'
              data-elm-id='auction_starts_value'
            >
              {provisionalSaleDate.format('MMM DD')},{' '}
              {moment(segment.start_date)
                .tz(blueprint.utils.calculateTimeZone())
                .format('hh:mm a z')}
              <CalendarPopup isInTour={isInTour} />
            </Text>
          ),
        },
  ]
  const ervRow = getERVRow(listing, isRedesignEnabled)
  ervRow && details.unshift(ervRow)
  return details
}

interface PendingProps {
  similarProperties?: any[]
  isInTour?: boolean
  featureFlags: FeatureFlags
  isRedesignEnabled?: boolean
}

const Pending = ({
  similarProperties,
  isInTour,
  featureFlags,
  isRedesignEnabled,
}: PendingProps) => {
  const { listing, segment } = useListingContext()
  const { property, propertyActions } = usePropertyContext()

  const shouldShowSealedBidTreatmentForListing = shouldShowSealedBidTreatment({
    segment,
    featureFlags,
  })

  const title = shouldShowSealedBidTreatmentForListing
    ? ''
    : listing.listing_status_label || ''

  let additionalContent
  if (blueprint.listing.utils.isFclOnlineAuction(listing)) {
    additionalContent = tpsAdditionalContent(
      hasSimilarProperties(similarProperties)
    )
  } else if (shouldShowSealedBidTreatmentForListing) {
    additionalContent = <SealedBidStatusMessageBox />
  }

  return (
    <AuctionDetailsTemplate
      title={title}
      primaryButton={
        <SavePropertyButton
          listing={listing}
          property={property}
          isSaved={property.is_currently_saved}
          actions={propertyActions}
          options={{
            text: SAVE_PROPERTY_CTA_TPS,
          }}
        />
      }
      additionalContent={additionalContent}
      detailContent={detailContent({
        isInTour,
        listing,
        segment,
        isRedesignEnabled,
      })}
    />
  )
}

export default Pending
